import { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Box } from "@mui/material";
import { Col, FloatingLabel, Form } from "react-bootstrap";
import { Typeahead, Hint } from "react-bootstrap-typeahead";

const AutocompleteAddressData = ({
    prefix = "",
    labelCity = "Choose a City...",
    labelProvince = "Select Province",
    labelCountry = "Select Country",
    control,
    register,
    setValue,
    errors,
    cityOptions,
    handleProvince,
    newHandleCountry,
    defaultCityID,
    defaultProvinceID,
    interlinerRowData,
    resetHandler
}) => {
    const [provinceList, setProvinceList] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const fieldCity = prefix ? `${prefix}City` : "cityID";
    const fieldProvince = prefix ? `${prefix}Province` : "provineStateID";
    const fieldCountry = prefix ? `${prefix}Country` : "countryId";
    const getFieldValue = (prefix, item, nameField, idField) => {
        return prefix ? item[nameField] : item[idField];
    };

    useEffect(() => {
        if (defaultCityID) {
            (async () => {
                try {
                    const pList = await handleProvince(defaultCityID);
                    setProvinceList(pList?.data?.data || []);
                    if (defaultProvinceID) {
                        const cList = await newHandleCountry(defaultProvinceID);
                        setCountryList(cList?.data?.data || []);
                    }
                    resetHandler();
                } catch (error) {
                    console.error("Error fetching province/country data on mount:", error);
                    setProvinceList([]);
                    setCountryList([]);
                }

            })();
        }
    }, [interlinerRowData]);

    return (
        <>
            <Col>
                <Controller
                    control={control}
                    name={fieldCity}
                    rules={{ required: "Field is required" }}
                    render={({ field, formState }) => (
                        <>
                            <Typeahead
                                placeholder={labelCity}
                                {...field}
                                name={fieldCity}
                                labelKey="name"
                                id={fieldCity}
                                options={cityOptions}
                                onChange={async (selected) => {
                                    field.onChange(selected);
                                    if (selected.length === 0) {
                                        setProvinceList([]);
                                        setCountryList([]);
                                    } else {
                                        try {
                                            const pList = await handleProvince(selected[0].id);
                                            const provinces = pList?.data?.data || [];
                                            setProvinceList(provinces);
                                            if (provinces.length === 1) {
                                                setValue(fieldProvince, getFieldValue(prefix, provinces[0], "provineSateName", "provineStateID"));
                                                const cList = await newHandleCountry(provinces[0].provineStateID);
                                                setCountryList(cList?.data?.data || []);
                                                if (cList?.data?.data?.length === 1) {
                                                    setValue(fieldCountry, getFieldValue(prefix, cList.data.data[0], "countryname", "countryId"));
                                                }
                                            }
                                        } catch (error) {
                                            console.error("Error fetching province data:", error);
                                            setProvinceList([]);
                                        }
                                    }
                                }}
                                selected={field.value || []}
                                renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
                                    <Box sx={{ ">div": { flexDirection: "column" } }}>
                                        <Hint>
                                            <FloatingLabel controlId="floatingLabel" label={labelCity}>
                                                <Form.Control
                                                    {...inputProps}
                                                    value={inputProps.value || ""}
                                                    ref={(node) => {
                                                        inputRef(node);
                                                        referenceElementRef(node);
                                                    }}
                                                />
                                            </FloatingLabel>
                                        </Hint>
                                    </Box>
                                )}
                            />
                            {formState?.errors?.[fieldCity] && (
                                <p className="error-message">
                                    {formState.errors[fieldCity].message}
                                </p>
                            )}
                        </>
                    )}
                />
            </Col>
            <Col>
                <Form.Group controlId={fieldProvince}>
                    <FloatingLabel controlId="floatingSelectGrid" label={labelProvince}>
                        <Form.Select
                            aria-label="Floating label select example"
                            name={fieldProvince}
                            {...register(fieldProvince, {
                                required: "Field is required",
                                onChange: async (e) => {
                                    let provinceId = e.target.value;
                                    if (prefix) {
                                        const matchingProvince = provinceList.find(
                                            (province) => province.provineSateName === e.target.value
                                        );
                                        provinceId = matchingProvince ? matchingProvince.provineStateID : "";
                                    }
                                    const cList = await newHandleCountry(provinceId);
                                    setCountryList(cList?.data?.data || []);
                                    if (cList?.data?.data?.length === 1) {
                                        setValue(fieldCountry, getFieldValue(prefix, cList.data.data[0], "countryname", "countryId"));
                                    }
                                },
                            })}
                        >
                            <option value="">Select</option>
                            {provinceList?.map((data, index) => (
                                <option key={index} value={prefix ? data.provineSateName : data.provineStateID}>
                                    {data.provineSateName}
                                </option>
                            ))}
                        </Form.Select>
                    </FloatingLabel>
                    {errors[fieldProvince] && (
                        <p className="error-message">
                            {errors[fieldProvince]?.message}
                        </p>
                    )}
                </Form.Group>
            </Col>
            <Col>
                <Form.Group controlId={fieldCountry}>
                    <FloatingLabel controlId="floatingSelectGrid" label={labelCountry}>
                        <Form.Select
                            aria-label="Floating label select example"
                            name={fieldCountry}
                            {...register(fieldCountry, { required: "Field is required" })}
                        >
                            <option value="">Select</option>
                            {countryList?.map((data, index) => (
                                <option key={index} value={prefix ? data.countryname : data.countryId}>
                                    {data.countryname}
                                </option>
                            ))}
                        </Form.Select>
                    </FloatingLabel>
                    {errors[fieldCountry] && (
                        <p className="error-message">
                            {errors[fieldCountry]?.message}
                        </p>
                    )}
                </Form.Group>
            </Col>
        </>
    );
};

export default AutocompleteAddressData;
